import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as authApi from '@/api/auth'
import { refreshOnce, setSessionExpiredHandler } from '@/api/client'
import { ApiError } from '@/api/errors'
import { clearTokens, getRefreshToken } from '@/api/session'
import type { CurrentEmployee } from '@/api/types'
import { useUiStore } from '@/stores/ui'

/**
 * 로그인 상태.
 *
 * 액세스 토큰은 `api/session.ts` 가 메모리에 들고 있다. 스토어는 **사용자**만 본다.
 * 토큰을 여기 복사해 두면 두 곳이 갈라진다.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentEmployee | null>(null)
  const booting = ref(true)
  const loginError = ref<string | null>(null)
  /** 서버 시각 − 브라우저 시각. 경과 시간 계산 보정에 쓴다. */
  const clockSkewMs = ref(0)

  const isAuthenticated = computed(() => null !== user.value)
  const roles = computed(() => user.value?.roles ?? [])
  const isManager = computed(() => roles.value.includes('ROLE_MANAGER'))
  const isAdmin = computed(() => roles.value.includes('ROLE_ADMIN'))

  /** 개인 → 기본 현장 → UTC. 화면에서 타임존을 고를 때의 기본값. */
  const defaultTimezone = computed(
    () => user.value?.timezone ?? user.value?.defaultWorkSite?.timezone ?? 'UTC',
  )

  function setUser(next: CurrentEmployee | null): void {
    user.value = next
  }

  function noteServerTime(iso: string): void {
    const parsed = Date.parse(iso)
    if (Number.isFinite(parsed)) {
      clockSkewMs.value = parsed - Date.now()
    }
  }

  async function loginWithGoogle(idToken: string): Promise<void> {
    loginError.value = null
    try {
      user.value = await authApi.loginWithGoogle(idToken)
      useUiStore().adoptAccountLocale(user.value.locale)
    } catch (error) {
      // 서버 문구를 그대로 보여준다 (CLAUDE.md 7).
      loginError.value = error instanceof ApiError ? error.message : String(error)
      throw error
    }
  }

  /**
   * 새로고침 복구. 액세스 토큰은 메모리라 사라졌지만 리프레시 토큰이 있으면
   * 세션을 되살릴 수 있다. 없으면 조용히 비로그인 상태로 둔다.
   */
  async function restore(): Promise<void> {
    booting.value = true
    try {
      if (null === getRefreshToken()) {
        user.value = null

        return
      }

      await refreshOnce()
      user.value = await authApi.fetchMe()
      // 새 기기·다시 설치한 브라우저면 여기서 계정에 저장된 언어를 따라간다.
      useUiStore().adoptAccountLocale(user.value.locale)
    } catch {
      // 재사용 감지·만료 등. 로그인 화면으로 보내는 것 말고 할 일이 없다.
      clearTokens()
      user.value = null
    } finally {
      booting.value = false
    }
  }

  async function logout(): Promise<void> {
    await authApi.logout()
    user.value = null
  }

  /** 인터셉터가 세션 종료를 알리면 사용자만 비운다. 라우팅은 가드가 한다. */
  setSessionExpiredHandler(() => {
    user.value = null
  })

  return {
    user,
    booting,
    loginError,
    clockSkewMs,
    isAuthenticated,
    roles,
    isManager,
    isAdmin,
    defaultTimezone,
    setUser,
    noteServerTime,
    loginWithGoogle,
    restore,
    logout,
  }
})
